import _ from "lodash";
import moment from "moment";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";
import { getGradingSetupConfigurations } from "GradingSetupV2/modules/Utils";

const _getOrganizationPointSetByAcademicTermId = ({
  organizationPointSet,
  termId,
}) => {
  return _.get(
    _.first(
      _.filter(organizationPointSet, item => {
        return _.find(
          _.get(item, "node.associatedEntities"),
          associatedEntity => {
            return _.isEqual(_.get(associatedEntity, "id"), termId);
          }
        );
      })
    ),
    "node",
    {}
  );
};

export const getOrganizationPointSetByAcademicTermId = _.memoize(
  _getOrganizationPointSetByAcademicTermId,
  params => JSON.stringify(params)
);

const _getOrganizationPointSetData = ({ organizationPointSet }) => {
  return _.get(_.first(organizationPointSet), "node", {});
};

export const getOrganizationPointSetData = _.memoize(
  _getOrganizationPointSetData,
  params => JSON.stringify(params)
);

const _getTermOption = (term, t) => {
  if (!term) return null;
  const startDate = moment(_.get(term, "startDate")).format("D MMM YYYY");
  const endDate = moment(_.get(term, "endDate")).format("D MMM YYYY");
  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(term);

  return {
    key: term.id,
    value: term.id,
    label: `${term.label} (${startDate} - ${endDate})`,
    disabled:
      !isGradingPeriodUnlocked ||
      (_.has(term, "isDisabled") ? term.isDisabled : false),
    isGradingPeriodUnlocked: isGradingPeriodUnlocked,
    disabledTooltip: !isGradingPeriodUnlocked
      ? t(
          "academicSetup:grading_period_is_locked_new_assessments_cannot_be_associated_with_it"
        )
      : null,
  };
};

export const getTermOption = _.memoize(_getTermOption, params =>
  JSON.stringify(params)
);

export const getNextUnlockedTerm = ({ termsOptions, selectedTerm }) => {
  const selectedIndex = _.findIndex(termsOptions, {
    key: _.get(selectedTerm, "key"),
  });

  return _.find(_.slice(termsOptions, selectedIndex + 1), {
    isGradingPeriodUnlocked: true,
  });
};

export const getDisabledCategoryDropdownTooltip = ({
  skipSubjectCheck,
  selectedTerm,
  selectedSubjectId,
  subjectsField,
  t,
}) => {
  if (_.isEmpty(selectedTerm)) {
    return t("classRoom:please_select_a_grading_period_to_enable_categories");
  }

  if (!skipSubjectCheck) {
    if (_.isEmpty(selectedSubjectId)) {
      return t("classRoom:please_select_a_subject_to_enable_categories");
    }

    if (_.size(subjectsField?.value) > 1) {
      return t("classRoom:categories_can_only_be_tagged_to_single_subject");
    }
  }

  return t("classRoom:no_categories_associated_with_term");
};

export const getCommonCategoriesFromGradingSetup = ({
  category,
  gradingConfigurations,
  selectedGradingPeriodId,
  curriculumProgramId,
}) => {
  const categoriesFromGradingConfigurations = _.map(
    gradingConfigurations,
    gradingConfiguration => {
      const gradingCategorySets = _.get(
        gradingConfiguration,
        "gradingCategorySets",
        []
      );

      const {
        differentCategoriesForGradingPeriods,
        isCategoriesEnabled,
      } = getGradingSetupConfigurations({
        curriculumProgramId,
        scoreGradingConfig: gradingConfiguration,
        isUsingNewGradingSetupInCourses: true,
      });

      if (!isCategoriesEnabled) {
        return [];
      }

      const defaultGradingCategorySet = _.find(gradingCategorySets, set =>
        _.isEmpty(_.get(set, "academicTerm.id"))
      );

      const filteredSet = differentCategoriesForGradingPeriods
        ? _.find(
            gradingCategorySets,
            set => _.get(set, "academicTerm.id") === selectedGradingPeriodId
          )
        : defaultGradingCategorySet;

      const gradingCategoryValues = _.get(
        filteredSet,
        "gradingCategoryValues",
        []
      );

      return _.map(gradingCategoryValues, category => {
        const { globalCategory } = category || {};

        return {
          id: _.get(globalCategory, "id"),
          label: _.get(globalCategory, "name"),
        };
      });
    }
  );

  const commonCategories = _.intersectionBy(
    ...categoriesFromGradingConfigurations,
    "id"
  );

  if (!_.isNil(category?.id)) {
    commonCategories.push(category);
  }

  return _.uniqBy(commonCategories, "id");
};

export const getCategoryInputForUpdateAssessment = ({
  updatedCategoryId,
  currentCategoryId,
  isUsingNewGradingSetupInCourses,
}) => {
  if (!isUsingNewGradingSetupInCourses) {
    return {
      categoryId: updatedCategoryId,
    };
  }

  const addedCategories = [];
  const removedCategories = [];

  if (!_.isEmpty(updatedCategoryId)) {
    addedCategories.push(updatedCategoryId);
  }

  if (!_.isEmpty(currentCategoryId)) {
    removedCategories.push(currentCategoryId);
  }

  return {
    globalCategories: {
      addedCategories,
      removedCategories,
    },
  };
};

export const getCategoryObj = ({
  globalCategories,
  category,
  isUsingNewGradingSetupInCourses,
}) => {
  return isUsingNewGradingSetupInCourses
    ? {
        id: _.get(globalCategories, "0.id"),
        label: _.get(globalCategories, "0.name"),
      }
    : category;
};
